import React, { useState, useRef } from "react";
import "../Login.css";
import { apiFetch } from "../utils/api";

const configuredCustomerPortalUrl = String(process.env.REACT_APP_CUSTOMER_PORTAL_URL || "").trim();

const normalizedCustomerPortalUrl = (() => {
  if (!configuredCustomerPortalUrl) return "";
  if (
    /^https?:\/\//i.test(configuredCustomerPortalUrl) ||
    configuredCustomerPortalUrl.startsWith("/") ||
    configuredCustomerPortalUrl.startsWith("#")
  ) {
    return configuredCustomerPortalUrl;
  }

  // Accept host/path values like booking.example.com and normalize to https.
  if (/^[\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(configuredCustomerPortalUrl)) {
    return `https://${configuredCustomerPortalUrl}`;
  }

  return "";
})();

const Login = ({ onLogin }) => {
  const [sessionNotice] = useState(() => {
    if (typeof window === "undefined") return "";
    try {
      const isExpired = localStorage.getItem("dw-session-expired") === "1";
      if (!isExpired) return "";
      localStorage.removeItem("dw-session-expired");
      return "Session expired. Please log in again.";
    } catch {
      return "";
    }
  });
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const submitInFlightRef = useRef(false);
  const customerPortalHref = normalizedCustomerPortalUrl || "#book";
  const isExternalPortal = /^https?:\/\//i.test(customerPortalHref);

  const handleCustomerPortalClick = async e => {
    if (!isExternalPortal) return;

    e.preventDefault();
    try {
      // no-cors lets us test network reachability for cross-origin portals.
      await fetch(customerPortalHref, {
        method: "GET",
        mode: "no-cors",
        cache: "no-store"
      });
      window.location.href = customerPortalHref;
    } catch {
      window.location.hash = "book";
    }
  };

  const handleChange = e => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const submit = async e => {
    e.preventDefault();
    if (submitInFlightRef.current || loading) return;
    
    submitInFlightRef.current = true;
    try {
      setLoading(true);
      setError("");
      const attemptLogin = async forceLogoutOther => {
        let res, data;
        try {
          res = await apiFetch("/api/auth/login", {
            method: "POST",
            body: JSON.stringify({ ...credentials, forceLogoutOther })
          });
          data = await res.json().catch(() => null);
        } catch (fetchErr) {
          throw new Error(`Network error: ${fetchErr.message}. Check your connection and API base URL.`);
        }

        if (res.status === 409 && data?.code === "SESSION_ALREADY_ACTIVE" && !forceLogoutOther) {
          const confirmed = window.confirm(
            data?.confirmMessage || "This user is already logged in another system. Logout from other system and continue?"
          );
          if (!confirmed) return null;
          return attemptLogin(true);
        }

        if (!res.ok) {
          const message = data?.error || `Request failed with status ${res.status}`;
          throw new Error(message);
        }

        if (!data || typeof data !== "object") {
          console.error("[Login] Invalid API response format:", { data, contentType: res.headers?.get("content-type") });
          throw new Error("Login failed: API returned invalid format. Verify the API base URL and backend service.");
        }

        if (!data?.token || !data?.user) {
          console.error("[Login] Missing required fields in response:", { hasToken: !!data?.token, hasUser: !!data?.user, dataKeys: Object.keys(data || {}) });
          throw new Error("Login failed: API response missing token or user data. Backend may have returned an error page.");
        }

        return data;
      };

      const result = await attemptLogin(false);
      if (!result) return;
      onLogin(result);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
      submitInFlightRef.current = false;
    }
  };

  return (
    <div className="login-container">
      <form className="login-form" onSubmit={submit}>
        <h2>Sign In</h2>
        <div className="input-group">
          <label htmlFor="username">Username</label>
          <input
            type="text"
            name="username"
            id="username"
            value={credentials.username}
            onChange={handleChange}
            required
          />
        </div>
        <div className="input-group">
          <label htmlFor="password">Password</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              id="password"
              value={credentials.password}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="password-toggle-btn"
              onClick={() => setShowPassword(prev => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? "🙈" : "👁"}
            </button>
          </div>
        </div>
        <button type="submit" className="login-btn" disabled={loading}>{loading ? "Signing In..." : "Log In"}</button>
        <a
          href={customerPortalHref}
          className="login-hint"
          style={{ display: "inline-block", marginTop: 10 }}
          onClick={handleCustomerPortalClick}
        >
          Customer booking portal
        </a>
        {sessionNotice && <p className="login-hint">{sessionNotice}</p>}
        {error && <p className="login-error">{error}</p>}
       
      </form>
    </div>
  );
};

export default Login;